import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  CSSProperties,
  FC,
  useCallback
} from 'react';
import { styled, Tooltip as MaterialTooltip, TooltipProps, tooltipClasses } from '@mui/material';
import clsx from 'clsx';
import { TooltipMode } from './interfaces';
import {
  getTooltipBorderStyle,
  getTooltipBoxShadow,
  getTooltipColor,
  isSecondaryTooltip,
  isSurfaceTooltip,
  tooltipPlacementMargin
} from './utils';
import { hasOverflowingText } from '../shared/utils/dom';

export interface TooltipExtendedProps extends TooltipProps {
  dataId: string;
  enableOnTextOverflow?: boolean;
  disableInteractive?: boolean;
  alwaysStayOpen?: boolean;
  disabled?: boolean;
  mode?: TooltipMode;
  maxWidth?: string;
  maxHeight?: string;
  minHeight?: string;
  forceOpen?: boolean;
  forceClose?: boolean;
  onOpenCallback?: VoidFunction;
  onCloseCallback?: VoidFunction;
  extraOffsetX?: number;
  extraOffsetY?: number;
  customStyle?: CSSProperties;
  tooltipContentClass?: string;
  titleProps?: object;
  zIndex?: number;
}

export const Tooltip = styled(
  ({
    dataId,
    enableOnTextOverflow = false,
    disableInteractive = true,
    alwaysStayOpen = false,
    disabled = false,
    forceOpen = false,
    forceClose = false,
    onOpenCallback = () => {},
    onCloseCallback = () => {},
    extraOffsetX,
    extraOffsetY,
    tooltipContentClass = '',
    titleProps = {},
    zIndex,
    title,
    className,
    ...otherProps
  }: TooltipExtendedProps) => {
    const [tooltipDisabled, setTooltipDisabled] = useState(false);
    const contentRef = useRef<HTMLElement | null>(null);
    const [open, setOpen] = useState(forceOpen);

    useLayoutEffect(() => {
      if (enableOnTextOverflow && contentRef.current !== null) {
        const isContentEllipsized = hasOverflowingText(contentRef.current!, document);
        setTooltipDisabled(!isContentEllipsized);
      }
    }, [enableOnTextOverflow]);

    useEffect(() => {
      setOpen(forceOpen);
    }, [forceOpen]);

    useEffect(() => {
      if (forceClose) {
        setOpen(false);
      }
    }, [forceClose]);

    const openTooltip = () => {
      setOpen(true);

      onOpenCallback();
    };

    const closeTooltip = () => {
      setOpen(false);
      onCloseCallback();
    };

    const handleTooltipClick = useCallback((e: React.MouseEvent) => {
      e.stopPropagation();
    }, []);

    const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        e.stopPropagation();
      }
    }, []);

    const TitleComponent: FC<{ onClose: VoidFunction }> =
      typeof title === 'function' ? title : () => title;

    if (disabled) {
      return otherProps.children;
    }

    return (
      <MaterialTooltip
        arrow
        open={!!(open || alwaysStayOpen)}
        onOpen={openTooltip}
        onClose={closeTooltip}
        {...otherProps}
        ref={contentRef}
        disableInteractive={disableInteractive}
        classes={{ popper: className }}
        title={
          !title || tooltipDisabled ? (
            ''
          ) : (
            <div
              className={clsx('content', tooltipContentClass)}
              data-id={dataId}
              onClick={handleTooltipClick}
              onMouseDown={handleTooltipClick}
              onKeyDown={handleKeyDown}
              tabIndex={-1}
            >
              <TitleComponent onClose={closeTooltip} {...titleProps} />
            </div>
          )
        }
      />
    );
  }
)(({
  theme,
  mode,
  maxWidth,
  maxHeight,
  minHeight,
  extraOffsetX,
  extraOffsetY,
  zIndex,
  customStyle = {}
}) => {
  const color = getTooltipColor(theme, mode);

  return {
    '&': {
      zIndex:
        typeof zIndex !== 'undefined' && zIndex !== null ? zIndex : 'var(--zindex-tooltip-react)'
    },
    [`& .${tooltipClasses.arrow}`]: {
      color,
      '&::before': {
        border: isSecondaryTooltip(mode) ? 'solid 1px var(--divider-color)' : 'none'
      }
    },
    [`& .${tooltipClasses.tooltip}`]: {
      backgroundColor: color,
      width: 'auto',
      maxWidth: maxWidth || '256px',
      maxHeight: maxHeight || '500px',
      minHeight: minHeight || 'auto',
      display: 'flex',
      flexDirection: 'column',
      position: 'relative',
      color: isSurfaceTooltip(mode) ? 'var(--text-default-color)' : 'var(--light-text)',
      backgroundClip: 'padding-box',
      border: getTooltipBorderStyle(mode),
      borderRadius: isSecondaryTooltip(mode) ? 'var(--border-radius-6)' : 'var(--border-radius-4)',
      boxShadow: getTooltipBoxShadow(mode),
      transition: 'all 200ms ease',
      wordWrap: 'break-word',
      fontSize: 'var(--font-size-body)',
      fontWeight: 'var(--font-weight-normal)',
      lineHeight: '16px',
      fontFamily: 'var(--font-family) !important',
      fontStyle: 'normal',
      padding: '0',
      ...customStyle
    },
    [`& .${tooltipClasses.tooltip} .content`]: {
      padding: isSecondaryTooltip(mode)
        ? 'calc(var(--spacer) * 0.25) calc(var(--spacer) * 0.75)'
        : 'calc(var(--spacer) * 0.25) calc(var(--spacer) * 0.5)',
      overflow: 'auto'
    },
    [`&.light .${tooltipClasses.tooltip}`]: {
      backgroundColor: 'var(--surface-tooltip-bg-color)',
      color: 'var(--text-default-color)'
    },
    [`&.light .${tooltipClasses.arrow}`]: {
      color: 'var(--surface-tooltip-bg-color)'
    },
    ...tooltipPlacementMargin(extraOffsetX, extraOffsetY)
  };
});
